import React, { useState, useEffect } from 'react';
import { FaTimes, FaPlus, FaTrashAlt } from 'react-icons/fa';
import { getEmailMonitorSettings, updateEmailMonitorSettings } from '../../services/emailMonitorService';

// Who the monitor watches, and whether the AI is allowed to write anything.
//
// The mailboxes themselves are shown read-only on purpose: Graph will only serve a mailbox the Azure
// app registration was scoped to, so an address typed in here would just produce 403s at poll time.

const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const AddressList = ({ title, hint, addresses, onChange, disabled }) => {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState(null);

  const add = () => {
    const email = draft.trim().toLowerCase();
    if (!email) return;
    if (!isEmail(email)) { setError('That does not look like an email address.'); return; }
    if (addresses.includes(email)) { setError('Already on the list.'); return; }
    onChange([...addresses, email]);
    setDraft('');
    setError(null);
  };

  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
      <p className="text-xs text-gray-500 mb-2">{hint}</p>

      <div className="flex gap-2 mb-2">
        <input
          type="email"
          value={draft}
          disabled={disabled}
          onChange={(e) => { setDraft(e.target.value); setError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="name@example.com"
          className="flex-1 text-sm border border-gray-300 rounded px-3 py-2 focus:ring-jims-cyan focus:border-jims-cyan"
        />
        <button
          type="button"
          onClick={add}
          disabled={disabled || !draft.trim()}
          className="px-3 py-2 text-sm font-medium text-white bg-jims-cyan rounded hover:bg-jims-cyan-dark disabled:opacity-50 inline-flex items-center gap-2"
        >
          <FaPlus className="text-xs" /> Add
        </button>
      </div>
      {error && <p className="text-xs text-red-700 mb-2">{error}</p>}

      {addresses.length === 0 ? (
        <p className="text-xs text-gray-400 italic">None yet.</p>
      ) : (
        <ul className="border border-gray-200 rounded divide-y divide-gray-100 max-h-40 overflow-y-auto">
          {addresses.map((email) => (
            <li key={email} className="flex items-center justify-between px-3 py-2 text-sm">
              <span className="text-gray-700 truncate">{email}</span>
              <button
                type="button"
                onClick={() => onChange(addresses.filter((a) => a !== email))}
                disabled={disabled}
                aria-label={`Remove ${email}`}
                className="text-gray-400 hover:text-red-600 disabled:opacity-50 shrink-0 ml-3"
              >
                <FaTrashAlt />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const EmailMonitorSettingsModal = ({ isOpen, onClose, onSaved }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getEmailMonitorSettings()
      .then((data) => { if (!cancelled) setSettings(data); })
      .catch((e) => { if (!cancelled) setError(e.message || 'Could not load the settings.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen]);

  if (!isOpen) return null;

  const patch = (changes) => setSettings((prev) => ({ ...prev, ...changes }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const saved = await updateEmailMonitorSettings({
        allowedSenders: settings.allowedSenders,
        excludedSenders: settings.excludedSenders,
        autoDraftReply: settings.autoDraftReply,
        autoSendReply: settings.autoSendReply,
        lookbackHours: settings.lookbackHours,
        provider: settings.provider,
        intentConfidenceThreshold: settings.intentConfidenceThreshold,
        issueConfirmThreshold: settings.issueConfirmThreshold,
        faqMatchThreshold: settings.faqMatchThreshold,
        bodyChars: settings.bodyChars,
      });
      onSaved?.(saved);
      onClose();
    } catch (e) {
      setError(e.message || 'Could not save the settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Email monitor settings</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-700">
            <FaTimes />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto space-y-6">
          {loading ? (
            <p className="text-sm text-gray-500">Loading&hellip;</p>
          ) : !settings ? (
            <p className="text-sm text-red-700">{error || 'Settings unavailable.'}</p>
          ) : (
            <>
              <div>
                <h4 className="text-sm font-semibold text-gray-800">Mailboxes being monitored</h4>
                <p className="text-xs text-gray-500 mb-2">
                  Set on the server, because Microsoft only grants access to mailboxes the app was approved for.
                </p>
                <ul className="text-sm text-gray-700 list-disc list-inside">
                  {(settings.mailboxes || []).map((m) => <li key={m}>{m}</li>)}
                </ul>
              </div>

              <AddressList
                title="Always monitor these senders"
                hint="Watched even without a Loope account, e.g. a franchisee writing from a personal address."
                addresses={settings.allowedSenders || []}
                onChange={(allowedSenders) => patch({ allowedSenders })}
                disabled={saving}
              />

              <AddressList
                title="Never monitor these senders"
                hint="Ignored completely. Wins over the list above if an address is on both."
                addresses={settings.excludedSenders || []}
                onChange={(excludedSenders) => patch({ excludedSenders })}
                disabled={saving}
              />

              <div>
                <h4 className="text-sm font-semibold text-gray-800">How far back to check</h4>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    max="720"
                    value={settings.lookbackHours}
                    disabled={saving}
                    onChange={(e) => patch({ lookbackHours: Number(e.target.value) })}
                    className="w-24 text-sm border border-gray-300 rounded px-3 py-2 focus:ring-jims-cyan focus:border-jims-cyan"
                  />
                  <span className="text-sm text-gray-600">hours (24 = the past day)</span>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-800">Who judges incoming mail</h4>
                <p className="text-xs text-gray-500 mb-2">
                  Gemini judges as today. Jev judges the topic and whether something is broken instead
                  (Jev only ever judges — drafts are still written by Gemini, and a human still sends).
                </p>
                <select
                  value={settings.provider || 'jev'}
                  disabled={saving}
                  onChange={(e) => patch({ provider: e.target.value })}
                  className="text-sm border border-gray-300 rounded px-3 py-2 focus:ring-jims-cyan focus:border-jims-cyan"
                  aria-label="Classifier"
                >
                  <option value="jev">Jev (judges)</option>
                  <option value="gemini">Gemini (previous behaviour)</option>
                </select>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-800">Certainty cut-offs</h4>
                <p className="text-xs text-gray-500 mb-2">
                  Below these the monitor asks a human instead of acting sure. Start with the defaults;
                  tune after watching real mail.
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min="0" max="1" step="0.05"
                      value={settings.intentConfidenceThreshold ?? 0.75}
                      disabled={saving}
                      onChange={(e) => patch({ intentConfidenceThreshold: Number(e.target.value) })}
                      className="w-24 text-sm border border-gray-300 rounded px-3 py-2 focus:ring-jims-cyan focus:border-jims-cyan"
                    />
                    <span className="text-sm text-gray-600">classify below this → needs review</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min="0.5" max="1" step="0.05"
                      value={settings.issueConfirmThreshold ?? 0.65}
                      disabled={saving}
                      onChange={(e) => patch({ issueConfirmThreshold: Number(e.target.value) })}
                      className="w-24 text-sm border border-gray-300 rounded px-3 py-2 focus:ring-jims-cyan focus:border-jims-cyan"
                    />
                    <span className="text-sm text-gray-600">call it an issue above this (mirrored below for enquiries)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min="0" max="1" step="0.05"
                      value={settings.faqMatchThreshold ?? 0.7}
                      disabled={saving}
                      onChange={(e) => patch({ faqMatchThreshold: Number(e.target.value) })}
                      className="w-24 text-sm border border-gray-300 rounded px-3 py-2 focus:ring-jims-cyan focus:border-jims-cyan"
                    />
                    <span className="text-sm text-gray-600">answer from knowledge below this → generic holding reply</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min="500" max="4000" step="100"
                      value={settings.bodyChars ?? 2000}
                      disabled={saving}
                      onChange={(e) => patch({ bodyChars: Number(e.target.value) })}
                      className="w-24 text-sm border border-gray-300 rounded px-3 py-2 focus:ring-jims-cyan focus:border-jims-cyan"
                    />
                    <span className="text-sm text-gray-600">email characters read for judging + drafts</span>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-semibold text-gray-800 mb-2">AI replies</h4>
                <label className="flex items-start gap-3 mb-3">
                  <input
                    type="checkbox"
                    checked={!!settings.autoDraftReply}
                    disabled={saving}
                    onChange={(e) => patch({ autoDraftReply: e.target.checked, autoSendReply: e.target.checked ? settings.autoSendReply : false })}
                    className="mt-1"
                  />
                  <span className="text-sm text-gray-700">
                    <span className="font-medium">Let the AI write a draft reply</span>
                    <span className="block text-xs text-gray-500">
                      Off by default. The monitor still reads and sorts every email either way; this only decides
                      whether it also suggests wording.
                    </span>
                  </span>
                </label>

                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={!!settings.autoSendReply}
                    disabled={saving || !settings.autoDraftReply}
                    onChange={(e) => patch({ autoSendReply: e.target.checked })}
                    className="mt-1"
                  />
                  <span className="text-sm text-gray-700">
                    <span className="font-medium">Send AI replies without review</span>
                    <span className="block text-xs text-gray-500">
                      {settings.autoDraftReply
                        ? 'Replies would go straight to franchisees and suppliers with nobody reading them first.'
                        : 'Needs AI drafting turned on first.'}
                    </span>
                  </span>
                </label>
              </div>

              {error && <p className="text-sm text-red-700">{error}</p>}
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || loading || !settings}
            className="px-4 py-2 text-sm font-medium text-white bg-jims-cyan rounded hover:bg-jims-cyan-dark disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailMonitorSettingsModal;
